import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';
import type { LegendItem } from '../LegendItems/LegendItem.types';

export const useLegendOverflow = (
  holder: RefObject<HTMLDivElement | null>,
  legend_items: readonly LegendItem[],
): { overflowing: number[]; isMeasured: boolean } => {
  const [overflowing, setOverflowing] = useState<number[]>([]);
  const [isMeasured, setIsMeasured] = useState(false);

  useLayoutEffect(() => {
    const root = holder.current;
    const itemList = root?.querySelector<HTMLDivElement>("[data-id='legend']");
    const moreButton = root?.querySelector<HTMLButtonElement>("[data-id='legend-more-button']");
    const menuList = root?.querySelector<HTMLUListElement>("[data-id='legend-menu']");
    if (!root || !itemList || !moreButton || !menuList) return;

    const measure = () => {
      const cells = Array.from(root.querySelectorAll<HTMLElement>('[data-legend-index]'));
      const rootStyle = getComputedStyle(root);
      const listStyle = getComputedStyle(itemList);
      const fullWidth = root.clientWidth
        - (parseFloat(rootStyle.paddingLeft) || 0)
        - (parseFloat(rootStyle.paddingRight) || 0);
      const gap = parseFloat(listStyle.columnGap) || 0;
      const widths = Array(legend_items.length).fill(0) as number[];
      for (const cell of cells) {
        const index = Number(cell.dataset.legendIndex);
        if (Number.isInteger(index) && index >= 0 && index < widths.length) {
          widths[index] = cell.getBoundingClientRect().width;
        }
      }
      const totalWidth = widths.reduce((total, width) => total + width, 0)
        + Math.max(0, widths.length - 1) * gap;
      const limit = totalWidth <= fullWidth
        ? fullWidth
        : fullWidth - moreButton.getBoundingClientRect().width - (parseFloat(rootStyle.columnGap) || 0);
      let usedWidth = 0;
      const first = widths.findIndex((width) => {
        const nextWidth = usedWidth + (usedWidth ? gap : 0) + width;
        if (nextWidth > limit) return true;
        usedWidth = nextWidth;
        return false;
      });
      const next = totalWidth <= fullWidth || first === -1
        ? []
        : widths.slice(first).map((_, index) => first + index);

      setOverflowing((current) =>
        current.length === next.length && current.every((value, index) => value === next[index])
          ? current
          : next,
      );
      setIsMeasured(true);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    observer.observe(itemList);
    observer.observe(moreButton);
    observer.observe(menuList);
    for (const cell of Array.from(root.querySelectorAll<HTMLElement>('[data-legend-index]'))) observer.observe(cell);
    return () => observer.disconnect();
  }, [holder, legend_items, overflowing]);

  return { overflowing, isMeasured };
};
